import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert, Box, Button, CircularProgress, Grid2, LinearProgress, TextField, ToggleButton, ToggleButtonGroup, Tooltip,
  Typography,
} from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";


import { useModules } from "../../context/ModuleContext";
import { ChangeChip, KpiCard, ListDrawer, pageLocally, sectionState, useSurface } from "./overview/OverviewUi";
import {
  AttendanceTrend, INACTIVE_META, IncomeChart, InactiveMembers, MembersByPackage, MembershipStatus, NewVsRenewals,
  RevenueDonuts,
} from "./overview/OverviewCharts";
import {
  AccessSummary, AlertsPanel, DeviceStatus, ExpiringSoon, PromotionsPanel, RecentCheckIns, RecentPayments,
  checkInColumns,
  paymentColumns,
} from "./overview/OverviewTables";
import {
  CATEGORY_META, METHOD_META, PALETTE, RANGE_PRESETS, compareLabel, customRangeError, downloadMonthlyReport, formatLkr,
  longDate, longMonth, overviewApi, periodLabel, plural, rangeFromParams, rangeText, requestError, shortDate,
  sriLankaToday, titleCase, weekdayDate,
} from "./overview/overviewUtils";

const REFRESH_MS = 60000;
const PAYMENTS_INFO = "Recorded payments only; registration payments recorded before activation are not included.";
// Drawers that show money (payments list, income month); only for finance viewers.
const isFinanceDrawer = (drawer) => drawer?.kind === "payments" || drawer?.kind === "month";

const EXPECTED_KIND = { renewal: "Renewal", registration: "New registration" };
const expectedColumns = [
  { field: "full_name", headerName: "Member", flex: 1.2, minWidth: 150, cellClassName: "name-column--cell" },
  { field: "membership_id", headerName: "Membership ID", flex: 1, minWidth: 140 },
  { field: "membership_type", headerName: "Package", flex: 1, minWidth: 130, valueGetter: (v) => titleCase(v) },
  {
    field: "kind", headerName: "Type", flex: 1, minWidth: 140,
    valueGetter: (v, row) => `${EXPECTED_KIND[v] || titleCase(v)}${row.renewed ? " (paid)" : ""}`,
  },
  { field: "due_date", headerName: "Due date", flex: 0.8, minWidth: 110, valueGetter: (v) => longDate(v) },
  {
    field: "amount", headerName: "Amount", flex: 0.9, minWidth: 130, align: "right", headerAlign: "right",
    valueGetter: (v, row) => (row.price_known ? formatLkr(v, 2) : "Price unknown"),
  },
];

const inactiveColumns = [
  { field: "full_name", headerName: "Member", flex: 1.2, minWidth: 150, cellClassName: "name-column--cell" },
  { field: "membership_id", headerName: "Membership ID", flex: 1, minWidth: 140 },
  {
    field: "last_check_in", headerName: "Last check-in", flex: 1, minWidth: 140,
    valueGetter: (v, row) => (row.never_checked_in ? "Never" : longDate(v)),
  },
  { field: "days_since", headerName: "Days since", flex: 0.7, minWidth: 110, valueGetter: (v) => plural(v, "day") },
];

function FilterBar({ range, today, onPreset, onCustom }) {
  const s = useSurface();
  const [from, setFrom] = useState(range.from);
  const [to, setTo] = useState(range.to);
  const [error, setError] = useState("");
  const [showCustom, setShowCustom] = useState(range.preset === "custom");
  useEffect(() => { setFrom(range.from); setTo(range.to); }, [range.from, range.to]);

  const apply = () => {
    const problem = customRangeError(from, to, today);
    setError(problem);
    if (!problem) onCustom(from, to);
  };
  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, mb: 2.5, borderRadius: 3, bgcolor: s.card, border: `1px solid ${s.border}` }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        <CalendarMonthOutlinedIcon sx={{ color: s.muted }} aria-hidden />
        <Box sx={{ overflowX: "auto", maxWidth: "100%" }}>
          <ToggleButtonGroup exclusive size="small" value={showCustom ? "custom" : range.preset}
            aria-label="Date range"
            onChange={(e, value) => {
              if (!value) return;
              if (value === "custom") { setShowCustom(true); return; }
              setShowCustom(false);
              setError("");
              onPreset(value);
            }}
            sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontSize: 13, px: 1.5, whiteSpace: "nowrap" },
              "& .Mui-selected": { bgcolor: `${PALETTE.blue}33 !important`, color: `${s.text} !important`, fontWeight: 700 } }}>
            {RANGE_PRESETS.map((p) => <ToggleButton key={p.value} value={p.value}>{p.label}</ToggleButton>)}
          </ToggleButtonGroup>
        </Box>
        <Typography sx={{ fontSize: 13, color: s.muted, ml: { md: "auto" } }}>
          <Box component="span" sx={{ fontWeight: 700, color: s.text }}>{longDate(range.from)} – {longDate(range.to)}</Box>
        </Typography>
      </Box>
      {showCustom && (
        <Box sx={{ display: "flex", gap: 1.5, mt: 1.5, flexWrap: "wrap", alignItems: "flex-start" }}>
          <TextField type="date" size="small" label="From" value={from} onChange={(e) => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }} inputProps={{ max: today }} sx={{ "& input": { fontSize: 13 } }} />
          <TextField type="date" size="small" label="To" value={to} onChange={(e) => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }} inputProps={{ max: today }} sx={{ "& input": { fontSize: 13 } }} />
          <Button variant="contained" onClick={apply} sx={{ textTransform: "none" }}>Apply</Button>
          {error && <Alert severity="warning" variant="outlined" sx={{ py: 0, fontSize: 13 }}>{error}</Alert>}
        </Box>
      )}
    </Box>
  );
}

export default function HpcOverview() {
  const s = useSurface();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isModuleEnabled } = useModules();
  const today = sriLankaToday();
  const range = useMemo(() => rangeFromParams(searchParams, today), [searchParams, today]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [expiringWindow, setExpiringWindow] = useState("7");
  const [drawer, setDrawer] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState("");
  const latestLoad = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++latestLoad.current;
    setLoading(true);
    try {
      const result = await overviewApi("", { from: range.from, to: range.to, preset: range.preset });
      if (requestId !== latestLoad.current) return;
      setData(result);
      setError(null);
      setRefreshKey((k) => k + 1);
    } catch (e) {
      if (requestId !== latestLoad.current) return;
      // Keep the last good data on screen; the banner says the refresh failed.
      setError({ message: requestError(e, "Could not load the HPU overview. Please try again."), sessionExpired: e?.response?.status === 401 });
    } finally {
      if (requestId === latestLoad.current) setLoading(false);
    }
  }, [range.from, range.to, range.preset]);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  // Operations Report PDF for the range chosen in the filter.
  const downloadReport = async () => {
    setReportLoading(true);
    setReportError("");
    try {
      await downloadMonthlyReport(range.from, range.to);
    } catch (e) {
      setReportError(requestError(e, "Could not create the report. Please try again."));
    } finally {
      setReportLoading(false);
    }
  };

  const setPreset = (preset) => setSearchParams({ range: preset }, { replace: false });
  const setCustom = (from, to) => setSearchParams({ range: "custom", from, to }, { replace: false });

  const failed = Boolean(error) && !data;
  const zkOn = isModuleEnabled("zkteco") && data?.zkteco_enabled !== false;
  // Financial panels only once the server confirms this user may see them (they fetch money endpoints).
  const financeOn = data?.finance_visible === true;
  const shownRange = data?.range ? { from: data.range.from, to: data.range.to } : range;
  const kpis = data?.kpis || {};
  // Preset of the data on screen (echoed by the server) so titles never run ahead of the numbers while loading.
  const kpiPreset = data?.range ? (data.range.preset || "custom") : range.preset;
  const kpiPeriod = periodLabel(kpiPreset, shownRange.from, shownRange.to, data?.today || today);
  // "Today" and "This month" keep their original cards; every other range uses the range KPIs.
  const todayMode = kpiPreset === "today";
  const monthMode = kpiPreset === "this_month";
  const attendanceSection = todayMode ? kpis.today_attendance : kpis.attendance_period;
  const paymentsSection = monthMode ? kpis.payments_this_month : kpis.payments_period;
  const attendance = attendanceSection || {};
  const payments = paymentsSection || {};
  const updatedAt = data?.as_of ? data.as_of.slice(11, 16) : null;
  const go = (path) => () => navigate(path);
  const openMember = (membershipId) => navigate(`/hpc/members/${encodeURIComponent(membershipId)}`);

  // ---------------------------------------------------------------- drawers
  useEffect(() => {
    if (!financeOn && isFinanceDrawer(drawer)) setDrawer(null);
  }, [financeOn, drawer]);

  const fetchPage = useCallback(async ({ page, pageSize, search }) => {
    if (!drawer || (!financeOn && isFinanceDrawer(drawer))) return { rows: [], total: 0 };
    if (drawer.kind === "day" || drawer.kind === "checkins") {
      const from = drawer.kind === "day" ? drawer.date : drawer.from;
      const to = drawer.kind === "day" ? drawer.date : drawer.to;
      const res = await overviewApi("/check-ins", { from, to, q: search, page, page_size: pageSize });
      if (res.available === false) return { rows: [], total: 0 };
      return {
        rows: res.items, total: res.total_count,
        summary: [plural(res.total_count, "member check-in"), plural(res.total_scans, "scan")],
      };
    }
    if (drawer.kind === "payments") {
      const res = await overviewApi("/payments", {
        from: drawer.from, to: drawer.to, method: drawer.method, category: drawer.category,
        q: search || drawer.search, page, page_size: pageSize,
      });
      return { rows: res.items, total: res.total_count, summary: [`Total ${formatLkr(res.total_amount, 2)}`, plural(res.total_count, "payment")] };
    }
    if (drawer.kind === "inactive") {
      const res = await overviewApi("/inactive", { bucket: drawer.bucket });
      if (res.available === false) return { rows: [], total: 0 };
      const paged = pageLocally(res.items, { page, pageSize, search },
        (row, q) => `${row.full_name} ${row.membership_id}`.toLowerCase().includes(q));
      return { ...paged, summary: [plural(res.count, "member")] };
    }
    return { rows: [], total: 0 };
  }, [drawer, financeOn]);

  const drawerConfig = useMemo(() => {
    if (!drawer || (!financeOn && isFinanceDrawer(drawer))) return null;
    const filterText = [drawer.method && drawer.method !== "all" ? METHOD_META[drawer.method]?.label : null,
      drawer.category && drawer.category !== "all" ? CATEGORY_META[drawer.category]?.label : null].filter(Boolean).join(" · ");
    switch (drawer.kind) {
      case "day":
        return {
          title: `Check-ins on ${weekdayDate(drawer.date)}`, subtitle: "Sri Lanka time",
          columns: checkInColumns(today), searchable: true, searchPlaceholder: "Name or membership ID",
          footer: <Button variant="contained" onClick={go(`/hpc/attendance?date=${drawer.date}`)} sx={{ textTransform: "none" }}>
            Open the attendance page for this day</Button>,
          emptyText: `No check-ins on ${longDate(drawer.date)}`,
        };
      case "checkins":
        return {
          title: "Check-ins", subtitle: `Between ${rangeText(drawer.from, drawer.to)}`, columns: checkInColumns(today),
          searchable: true, searchPlaceholder: "Name or membership ID", emptyText: "No check-ins",
        };
      case "payments":
        return {
          title: drawer.title || "Payments",
          subtitle: `Between ${rangeText(drawer.from, drawer.to)}${filterText ? ` · ${filterText}` : ""}`,
          columns: paymentColumns, searchable: true, searchPlaceholder: "Name, membership ID or receipt",
          emptyText: "No payments",
        };
      case "inactive":
        return {
          title: `Inactive ${INACTIVE_META[drawer.bucket].label}`, subtitle: "Active members",
          columns: inactiveColumns, searchable: true, searchPlaceholder: "Name or membership ID",
          emptyText: "Nobody in this group",
        };
      case "month": {
        const category = drawer.category !== "all" ? drawer.category : undefined;
        const method = drawer.method !== "all" ? drawer.method : undefined;
        const expectedTab = {
          key: "expected", label: "Expected", columns: expectedColumns, emptyText: "Nothing expected",
          fetchPage: async ({ page, pageSize, search }) => {
            const res = await overviewApi("/expected", { from: drawer.from, to: drawer.to, category });
            const unknown = res.items.filter((i) => !i.price_known).length;
            const paged = pageLocally(res.items, { page, pageSize, search },
              (row, q) => `${row.full_name} ${row.membership_id}`.toLowerCase().includes(q));
            return {
              ...paged,
              summary: [`Total ${formatLkr(res.total_amount, 2)}`, plural(res.total_count, "item"),
                ...(unknown ? [`${unknown} without a price`] : [])],
            };
          },
        };
        const receivedTab = {
          key: "received", label: "Received", columns: paymentColumns, emptyText: "No payments",
          searchPlaceholder: "Name, membership ID or receipt",
          fetchPage: async ({ page, pageSize, search }) => {
            const res = await overviewApi("/payments", {
              from: drawer.from, to: drawer.to, method, category, q: search, page, page_size: pageSize,
            });
            return { rows: res.items, total: res.total_count, summary: [`Total ${formatLkr(res.total_amount, 2)}`, plural(res.total_count, "payment")] };
          },
        };
        return {
          title: `Income for ${longMonth(drawer.key)}${drawer.upcoming ? " (upcoming)" : ""}`,
          subtitle: `${longDate(drawer.from)} – ${longDate(drawer.to)}${filterText ? ` · ${filterText}` : ""}`,
          searchable: true, searchPlaceholder: "Name or membership ID",
          tabs: drawer.upcoming ? [expectedTab] : [expectedTab, receivedTab],
        };
      }
      default:
        return null;
    }
  }, [drawer, today, financeOn]); // eslint-disable-line react-hooks/exhaustive-deps

  const openMonth = (bar, filters) => {
    if (!bar?.from || !financeOn) return;
    setDrawer({ kind: "month", key: bar.key, from: bar.from, to: bar.to, upcoming: bar.upcoming, ...filters });
  };

  const kpiCount = 2 + (zkOn ? 1 : 0) + (financeOn ? 1 : 0);
  const kpiWidth = { xs: 12, sm: 6, lg: 12 / kpiCount };

  return (
    <Box m={{ xs: "12px", sm: "16px", md: "20px" }} sx={{ minWidth: 0, overflowX: "hidden" }}>
      {/* Title */}
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 2, mb: 2 }}>
        <Box sx={{ flex: "1 1 260px", minWidth: 0 }}>
          <Typography variant="h3" sx={{ fontSize: { xs: 24, sm: 28 }, fontWeight: 800, color: s.text }}>HPU Overview</Typography>
          <Typography sx={{ fontSize: 14, color: s.muted }}>
            {financeOn ? "Membership, income and attendance" : "Membership and attendance"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: 13, color: s.muted }}>
            {updatedAt ? `Last updated ${updatedAt} (${data.timezone || "Asia/Colombo"})` : "Loading…"}
          </Typography>
          <Button variant="outlined" onClick={load} disabled={loading} sx={{ textTransform: "none", fontSize: 13 }}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshOutlinedIcon />}>
            Refresh
          </Button>
          <Button variant="contained" onClick={downloadReport} disabled={reportLoading}
            sx={{ textTransform: "none", fontSize: 13 }}
            startIcon={reportLoading ? <CircularProgress size={16} color="inherit" /> : <FileDownloadOutlinedIcon />}>
            {reportLoading ? "Preparing report…" : "Download report"}
          </Button>
        </Box>
      </Box>

      <FilterBar range={range} today={today} onPreset={setPreset} onCustom={setCustom} />
      {loading && data && <LinearProgress sx={{ mb: 1.5, borderRadius: 1 }} />}

      {reportError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setReportError("")}>{reportError}</Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}
          action={error.sessionExpired ? (
            <Button color="inherit" size="small" onClick={() => navigate("/login", { state: { from: location.pathname } })}>Log in</Button>
          ) : (
            <Button color="inherit" size="small" onClick={load} disabled={loading}>Retry</Button>
          )}>
          {error.message}{data ? " Showing the last loaded data." : ""}
        </Alert>
      )}

      {/* Row 1: KPIs (today / this month) */}
      <Grid2 container spacing={2} mb={2}>
        <Grid2 size={kpiWidth}>
          <KpiCard label="Active members" Icon={GroupOutlinedIcon} color={PALETTE.green}
            state={sectionState(kpis.active_members, failed)} reason={kpis.active_members?.reason}
            value={kpis.active_members?.value?.toLocaleString()} caption="Not frozen" />
        </Grid2>
        {zkOn && (
          <Grid2 size={kpiWidth}>
            <KpiCard label={todayMode ? "Today's attendance" : `Attendance · ${kpiPeriod}`}
              Icon={EventAvailableOutlinedIcon} color={PALETTE.blue}
              state={sectionState(attendanceSection, failed)} reason={attendance.reason}
              value={attendance.members?.toLocaleString()}
              change={<ChangeChip pct={attendance.change_pct}
                label={todayMode ? `vs ${weekdayDate(attendance.compare_to)}` : compareLabel(kpiPreset, attendance)} />}
              caption={todayMode ? plural(attendance.scans || 0, "scan") : `${plural(attendance.scans || 0, "scan")} in period`}
              onClick={todayMode ? go(`/hpc/attendance?date=${data?.today || today}`) : undefined} />
          </Grid2>
        )}
        {financeOn && (
          <Grid2 size={kpiWidth}>
            <KpiCard label={monthMode ? "Payments received this month" : `Payments received · ${kpiPeriod}`}
              Icon={PaymentsOutlinedIcon} color={PALETTE.purple}
              info={<Tooltip title={PAYMENTS_INFO} arrow><InfoOutlinedIcon sx={{ fontSize: 16, color: s.muted }} aria-label={PAYMENTS_INFO} /></Tooltip>}
              state={sectionState(paymentsSection, failed)} reason={payments.reason}
              value={formatLkr(payments.value)}
              change={<ChangeChip pct={payments.change_pct}
                label={monthMode ? "vs same days last month" : compareLabel(kpiPreset, payments)} />}
              caption={monthMode
                ? `${plural(payments.payments || 0, "payment")} since ${shortDate(payments.period_from)}`
                : `${plural(payments.payments || 0, "payment")} ${todayMode ? "today" : "in period"}`} />
          </Grid2>
        )}
        <Grid2 size={kpiWidth}>
          <KpiCard label="Expiring in 7 days" Icon={EventBusyOutlinedIcon} color={PALETTE.amber}
            state={sectionState(kpis.expiring_7_days, failed)} reason={kpis.expiring_7_days?.reason}
            value={kpis.expiring_7_days?.value?.toLocaleString()} caption="Active members"
            onClick={() => {
              setExpiringWindow("7");
              document.getElementById("hpu-expiring-soon")?.scrollIntoView({ behavior: "smooth" });
            }} />
        </Grid2>
      </Grid2>

      {/* Row 2: income */}
      {financeOn && (
        <Grid2 container spacing={2} mb={2}>
          <Grid2 size={12} sx={{ minWidth: 0 }}>
            <IncomeChart section={data?.income} failed={failed} range={shownRange} refreshKey={refreshKey} onMonth={openMonth} />
          </Grid2>
          {/* Full width so both donuts and their legends fit side by side on desktop */}
          <Grid2 size={12} sx={{ minWidth: 0 }}>
            <RevenueDonuts byMethod={data?.revenue_by_method} byCategory={data?.revenue_by_category} failed={failed}
              range={shownRange} />
          </Grid2>
          <Grid2 size={12} sx={{ minWidth: 0 }}>
            <PromotionsPanel section={data?.promotions} failed={failed} range={shownRange}
              onPromotion={(id) => navigate(`/hpc/promotions/${id}`)} onReport={go("/hpc/promotions/report")} />
          </Grid2>
        </Grid2>
      )}

      {/* Row 3: attendance and membership */}
      <Grid2 container spacing={2} mb={2}>
        {zkOn && (
          <Grid2 size={{ xs: 12, lg: 6 }} sx={{ minWidth: 0 }}>
            <AttendanceTrend section={data?.attendance_trend} failed={failed} range={shownRange}
              onDay={(date) => setDrawer({ kind: "day", date })} />
          </Grid2>
        )}
        <Grid2 size={{ xs: 12, md: 6, lg: zkOn ? 3 : 6 }} sx={{ minWidth: 0 }}>
          <MembershipStatus section={data?.membership_status} failed={failed} />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6, lg: zkOn ? 3 : 6 }} sx={{ minWidth: 0 }}>
          <NewVsRenewals section={data?.new_vs_renewals} failed={failed} />
        </Grid2>
      </Grid2>

      {/* Row 3b: members per package (no amounts, shown to every hpc_admin) */}
      <Grid2 container spacing={2} mb={2}>
        <Grid2 size={12} sx={{ minWidth: 0 }}>
          <MembersByPackage section={data?.members_by_package} failed={failed} />
        </Grid2>
      </Grid2>

      {/* Row 4: alerts, summary, device */}
      <Grid2 container spacing={2} mb={2}>
        <Grid2 size={{ xs: 12, md: 6, lg: zkOn ? 4 : 6 }} sx={{ minWidth: 0 }}>
          <AlertsPanel alerts={data?.alerts} failed={failed} zkOn={zkOn} go={go} />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6, lg: zkOn ? 4 : 6 }} sx={{ minWidth: 0 }}>
          <AccessSummary summary={data?.access_summary} failed={failed} zkOn={zkOn} />
        </Grid2>
        {zkOn && (
          <Grid2 size={{ xs: 12, lg: 4 }} sx={{ minWidth: 0 }}>
            <DeviceStatus device={data?.device_status} failed={failed} go={go} />
          </Grid2>
        )}
      </Grid2>

      {/* Row 5: activity tables */}
      {(zkOn || financeOn) && (
        <Grid2 container spacing={2} mb={2}>
          {zkOn && (
            <Grid2 size={{ xs: 12, xl: financeOn ? 6 : 12 }} sx={{ minWidth: 0 }}>
              <RecentCheckIns range={shownRange} today={data?.today || today} refreshKey={refreshKey} onMember={openMember}
                onViewAll={() => setDrawer({ kind: "checkins", from: shownRange.from, to: shownRange.to })} />
            </Grid2>
          )}
          {financeOn && (
            <Grid2 size={{ xs: 12, xl: zkOn ? 6 : 12 }} sx={{ minWidth: 0 }}>
              <RecentPayments range={shownRange} refreshKey={refreshKey} onMember={openMember}
                onViewAll={(filters) => setDrawer({ kind: "payments", from: shownRange.from, to: shownRange.to, ...filters })} />
            </Grid2>
          )}
        </Grid2>
      )}

      {/* Row 7: expiring + inactive */}
      <Grid2 container spacing={2}>
        <Grid2 size={{ xs: 12, lg: zkOn ? 8 : 12 }} sx={{ minWidth: 0 }}>
          <ExpiringSoon section={data?.expiring_soon} failed={failed} windowDays={expiringWindow}
            onWindow={setExpiringWindow} onMember={openMember} />
        </Grid2>
        {zkOn && (
          <Grid2 size={{ xs: 12, lg: 4 }} sx={{ minWidth: 0 }}>
            <InactiveMembers section={data?.inactive_members} failed={failed}
              onBucket={(bucket) => setDrawer({ kind: "inactive", bucket })} />
          </Grid2>
        )}
      </Grid2>

      <ListDrawer
        open={Boolean(drawer && drawerConfig)}
        onClose={() => setDrawer(null)}
        fetchPage={fetchPage}
        getRowId={(row) => row.id ?? row.registration_id}
        onRowClick={({ row }) => row.membership_id && openMember(row.membership_id)}
        {...(drawerConfig || { columns: [] })}
      />
    </Box>
  );
}
